import { Entity } from 'app/agriculture/queries/entities/types/Entity';
import { useSuspenseQueries } from '@tanstack/react-query';
import { useEntityQueryKey } from 'app/agriculture/queries/queryKey/useEntityQueryKey';
import { allMutableEntityTypes, entityConfig } from 'app/agriculture/mutations/types/entityConfig';
import { allEntityTypes } from 'app/agriculture/models/Entities';
import { useMemo } from 'react';
import { getEntities } from 'app/agriculture/queries/entities/getEntities';

/**
 * returns an array of all entities of all entity types, standardised to a simpler `Entity` type
 * @param entityTypes - filters the entity types to only those present in the array
 * */
export function useGenericEntities(
    entityTypes: (typeof allEntityTypes | typeof allMutableEntityTypes)[number][] = [
        ...new Set([...allEntityTypes, ...allMutableEntityTypes]),
    ],
) {
    const mutableEntityTypes = useMemo(
        () =>
            allMutableEntityTypes
                .filter((mutableEntityType) =>
                    entityTypes.some((entityType) => mutableEntityType === entityType),
                )
                .sort((a, b) => a.localeCompare(b)),
        [entityTypes],
    );

    const getQueryKey = useEntityQueryKey();
    const queryKeys = useMemo(
        () => mutableEntityTypes.map((entityType) => getQueryKey(entityType)),
        [getQueryKey, mutableEntityTypes],
    );

    const results = useSuspenseQueries({
        queries: queryKeys.map((queryKey) => ({
            queryKey,
            queryFn: getEntities,
        })),
    });

    return useMemo(() => {
        const genericEntities = new Map<Entity['id'], Entity>();

        mutableEntityTypes.forEach((entityType, index) => {
            const { genericEntityTransform } = entityConfig[entityType];
            const { data } = results[index] ?? {};
            if (!data) return;
            for (const [id, entity] of data) {
                genericEntities.set(id, genericEntityTransform.parse(entity));
            }
        });

        return genericEntities;
    }, [mutableEntityTypes, results]);
}
